import axios, { AxiosResponse } from 'axios';
import { CountryOption, StateOption, DonationFormData, ApiResponse } from '../types/donation';

// We'll set up environment-specific base URLs
const isProduction = process.env.NODE_ENV === 'production';

// These URLs should be replaced with actual API endpoints
const API_BASE_URL = isProduction 
  ? 'https://ykmainapi.yugalkunj.org/ykrestapi'  // Production URL (placeholder)
  : 'https://ykbaseapi.yugalkunj.org/ykrestapi';         // QA URL (placeholder)

// External Yugal Kunj API base (always absolute for dev and prod)
const YK_API_BASE =
  process.env.REACT_APP_YK_API_BASE || 'https://ykmainapi.yugalkunj.org/ykrestapi';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// API service functions
export const donationService = {
  // Verify Google reCAPTCHA token via YK API. Falls back to same-origin PHP endpoint in dev if needed.
  verifyRecaptcha: async (token: string): Promise<{ success: boolean; [k: string]: any }> => {
    // Primary: YK API endpoint
    const ykUrl = `${YK_API_BASE}/PaymentChannel/verify-recaptcha`;
    try {
      // Backend signature: VerifyCaptcha([FromBody] string captchaToken)
      // Send token as a JSON string literal so ASP.NET binds and ModSecurity is less likely to block
      const resp = await axios.post(
        ykUrl,
        JSON.stringify(token),
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          timeout: 15000,
        }
      );
      return (resp as any).data || { success: false };
    } catch (primaryErr: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Primary YK verify-recaptcha failed, falling back to local PHP endpoint...', primaryErr?.code || primaryErr?.message);
      }
      // Fallback: same-origin PHP endpoint for local testing
      try {
        const localUrl = '/recaptcha-verify.php';
        const resp2 = await axios.post(localUrl, { token }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        });
        return (resp2 as any).data || { success: false };
      } catch (fallbackErr: any) {
        console.error('reCAPTCHA verify failed (both YK and local):', fallbackErr?.response?.data || fallbackErr);
        return { success: false } as any;
      }
    }
  },
  // Get countries list from database
  getCountries: async (): Promise<CountryOption[]> => {
    try {
      // Use YK CommonChannel endpoint with proxy in dev and fallback to absolute host on timeout
      const primaryUrl = `${YK_API_BASE}/CommonChannel/GetCountry`;
      let payload: any;
      try {
        const resp = await axios.get(primaryUrl, { timeout: 30000 });
        payload = (resp as any).data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Primary GetCountry call failed, retrying absolute URL...', err?.code || err?.message);
        }
        // Fallback uses the same environment-specific base
        const absUrl = `${YK_API_BASE}/CommonChannel/GetCountry`;
        const resp2 = await axios.get(absUrl, { timeout: 30000 });
        payload = (resp2 as any).data;
      }
      const items = (payload?.data ?? payload ?? []) as Array<{
        id?: number | string;
        name?: string;
        CountryId?: number | string;
        CountryID?: number | string;
        countryId?: number | string;
        country_Id?: number | string; // new shape
        CountryName?: string;
        countryName?: string;
        country_Name?: string; // new shape
        CountryCode?: string;
        countryCode?: string;
        shortCode?: string | null; // new shape
      }>;
      if (!Array.isArray(items) || items.length === 0) {
        console.warn('GetCountry returned empty payload from', `${YK_API_BASE}/CommonChannel/GetCountry`, payload);
      }
      // Map to dropdown-friendly shape
      return items.map((c) => {
        const rawId = (c.id ?? c.CountryId ?? (c as any).CountryID ?? (c as any).countryId ?? (c as any).country_Id) as number | string | undefined;
        const idNum = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
        const id = Number.isFinite(idNum) ? idNum : 0;
        const name = (c.name ?? c.CountryName ?? (c as any).countryName ?? (c as any).country_Name) as string;
        const code = (c as any).shortCode ?? (c as any).CountryCode ?? (c as any).countryCode;
        return {
          id,
          name,
          // always use numeric id as string so downstream state API receives a valid number
          value: String(id),
          label: name,
          code,
        } as CountryOption;
      });
    } catch (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
  },

  // Get states list for a specific country using country code (kept for compatibility; uses GET)
  getStatesByCountryCode: async (countryCode: string): Promise<StateOption[]> => {
    try {
      const primaryUrl = `${YK_API_BASE}/CommonChannel/GetState`;
      let payload: any;
      try {
        const resp = await axios.get(primaryUrl, {
          params: { countryCode },
          timeout: 30000,
        });
        payload = (resp as any).data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Primary GetState(by code) failed, retrying absolute URL...', err?.code || err?.message);
        }
        // Fallback uses the same environment-specific base
        const absUrl = `${YK_API_BASE}/CommonChannel/GetState`;
        const resp2 = await axios.get(absUrl, {
          params: { countryCode },
          timeout: 30000,
        });
        payload = (resp2 as any).data;
      }
      const items = (payload?.data ?? payload ?? []) as Array<{
        id?: number | string;
        name?: string;
        StateId?: number | string;
        StateID?: number | string;
        userStateId?: number | string; // new shape
        StateName?: string;
        stateName?: string; // new shape
        CountryId?: number | string;
        country_Id?: number | string; // new shape
      }>; 
      if (!Array.isArray(items) || items.length === 0) {
        console.warn('GetState (by code) returned empty payload from', `${YK_API_BASE}/CommonChannel/GetState`, 'for countryCode', countryCode, payload);
      }
      return items.map((s) => {
        const rawSid = (s.id ?? s.StateId ?? (s as any).StateID ?? (s as any).userStateId) as number | string | undefined;
        const sid = typeof rawSid === 'string' ? parseInt(rawSid, 10) : Number(rawSid);
        const sname = (s.name ?? s.StateName ?? (s as any).stateName) as string;
        return {
          id: sid,
          name: sname,
          countryId: ((s as any).CountryId ?? (s as any).country_Id) as number,
          value: String(sid),
          label: sname,
        } as StateOption;
      });
    } catch (error) {
      console.error('Error fetching states by country code:', error);
      return [];
    }
  },

  // Get states list for a specific country from database (POST body as per API requirement)
  getStatesByCountry: async (countryId: number): Promise<StateOption[]> => {
    try {
      // Use YK CommonChannel endpoint with proxy in dev and fallback to absolute host on timeout
      const primaryUrl = `${YK_API_BASE}/CommonChannel/GetState`;
      const requestBody = { userStateId: 0, stateName: '', country_Id: countryId };
      let payload: any;
      try {
        const resp = await axios.post(primaryUrl, requestBody, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000,
        });
        payload = (resp as any).data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Primary GetState POST call failed, retrying absolute URL...', err?.code || err?.message);
        }
        // Fallback uses the same environment-specific base
        const absUrl = `${YK_API_BASE}/CommonChannel/GetState`;
        const resp2 = await axios.post(absUrl, requestBody, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000,
        });
        payload = (resp2 as any).data;
      }
      const items = (payload?.data ?? payload ?? []) as Array<{ id?: number; name?: string; StateId?: number; StateName?: string; CountryId?: number }>;
      if (!Array.isArray(items) || items.length === 0) {
        console.warn('GetState returned empty payload from', `${YK_API_BASE}/CommonChannel/GetState`, 'for countryId', countryId, payload);
      }
      // Map to dropdown-friendly shape and attach countryId context if missing
      return items.map((s) => {
        const rawSid = (s.id ?? s.StateId ?? (s as any).StateID ?? (s as any).userStateId) as number | string | undefined;
        const sid = typeof rawSid === 'string' ? parseInt(rawSid, 10) : Number(rawSid);
        const sname = (s.name ?? s.StateName ?? (s as any).stateName) as string;
        return {
          id: sid,
          name: sname,
          countryId: ((s as any).CountryId ?? (s as any).country_Id ?? countryId) as number,
          value: String(sid),
          label: sname,
        } as StateOption;
      });
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  },

  // Submit donation payment
  submitDonation: async (donationData: DonationFormData): Promise<ApiResponse> => {
    try {
      const response: AxiosResponse<ApiResponse> = await apiClient.post('/donation/payment', donationData);
      return response.data;
    } catch (error) {
      console.error('Error submitting donation:', error);
      return { success: false, message: 'Failed to process donation' };
    }
  },
  
  // Send email notification
  sendEmailNotification: async (donationData: DonationFormData): Promise<ApiResponse> => {
    try {
      const response: AxiosResponse<ApiResponse> = await apiClient.post('/donation/email-notification', donationData);
      return response.data;
    } catch (error) {
      console.error('Error sending email notification:', error);
      return { success: false, message: 'Failed to send email notification' };
    }
  },

  // One-Time Payment via AuthNet
  authNetOneTimePayment: async (payload: any): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/PaymentChannel/AuthNetOneTimePayment`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('AuthNet one-time payment error:', error?.response?.data || error);
      throw error;
    }
  },

  // Recurring Payment via AuthNet
  authNetRecurringPayment: async (payload: any, studentId: string = '0'): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/PaymentChannel/AuthNetRecurringPayment?studentId=${encodeURIComponent(studentId ?? '0')}`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('AuthNet recurring payment error:', error?.response?.data || error);
      throw error;
    }
  },

  // Send Email via MessageChannel (defaults aligned with C# sample)
  sendEmailMessage: async (
    data: {
      Monaris_Registration_Id?: number;
      Email: string;
      ContactNo: string;
      UserName: string;
      DonationDate?: string | Date;
      Amount: string;
      Address: string;
    },
    options?: { appName?: string; templateName?: string; orgId?: string | number }
  ): Promise<any> => {
    const { Email, ContactNo, UserName, DonationDate, Amount, Address } = data;
    const appName = options?.appName ?? 'Donation';
    const templateName = options?.templateName ?? 'RMSDonation';
    const orgId = options?.orgId ?? 3;

    // Build payload similar to FireRequestEmail in C#
    const payload = {
      emailFrom: 'string',
      emailBody: 'string',
      emailSubject: 'string',
      attachment: 'Y',
      userPhoneNumber: ContactNo,
      emailTo: Email,
      location: 'RMSDonation',
      appContent: {
        userName: String(UserName ?? ''),
        programName: 'string',
        appSchedule: 'string',
        attendee: 'string',
        date: (DonationDate ? new Date(DonationDate) : new Date()).toString(),
        orderType: 'string',
        orderNo: 'string',
        paymentMode: 'string',
        amount: Amount,
        email: Email,
        phone: 'string',
        pickupPerson: '',
        pickupPersonContact: `+${ContactNo}`,
        instructions: 'string',
        itemData: 'string',
        pickUpDate: 'string',
        pickUpTime: 'string',
        paymentType: 'string',
        billingAddress: 'string',
        address: String(Address ?? ''),
        pickupOrReceiver: 'string',
        orderReady: Amount,
        orderReadyLastLine: 'string',
        headingDate: 'string',
        headingTime: 'string',
      },
    };

    try {
      const url = `${YK_API_BASE}/MessageChannel/SendEmailMessage?appName=${encodeURIComponent(
        appName
      )}&templateName=${encodeURIComponent(templateName)}&orgId=${encodeURIComponent(String(orgId))}`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('SendEmailMessage error:', error?.response?.data || error);
      throw error;
    }
  },
};

export default donationService;
